import { ComponentFactoryResolver, Injectable } from "@angular/core";
import { lastValueFrom, Observable } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { EnvService } from "../core/env.service";
import { Alarm } from "../../components/notify/notify.component";

@Injectable({
  providedIn: "root",
})
export class InfluxService {
  private url: string;
  private urlIox: string;
  private urlCompute: string;
  // private urlIox = "http://localhost:5000";
  constructor(
    private http: HttpClient,
    private env: EnvService
  ) {
    this.url = this.env.pyApi;
    this.urlIox = this.env.ioxAdminApi;
    this.urlCompute = this.env.server;
  }

  public getLatestSensorValue(
    sensorId: string,
    measurement: string
  ): Observable<any> {
    const latestUrl =
      this.urlIox + "/api/v1/logs/" + measurement + "/" + sensorId;
    return this.http.get<any>(latestUrl);
  }

  getData(measurement, sensorId, from, to) {
    const url =
      this.urlIox + `/api/v1/logs/${measurement}/${sensorId}/${from}/${to}`;
    return this.http.get<any[]>(url);
  }

  getUsageData(measurement, field, sensorId, from, to) {
    const url =
      this.url +
      `/sv/ReportWorkHours/${measurement}` +
      `/${sensorId}/${field}/${from}/${to}`;
    return this.http.get<any[]>(url);
  }

  getSensorLogs(sensorIds: string[], measurement: string) {
    const url = `${this.urlIox}/api/v1/sensor_logs/${measurement}`;
    const body = { sensorIds: `['${sensorIds.join("', '")}']` };

    console.log("Fetching sensor logs:", url);
    const params = new HttpParams().set("sensorIds", JSON.stringify(sensorIds));

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post(url, body);
  }

  getSensorLogsByDate(sensorIds: string[], measurement: string, date: string) {
    // 2024-11-15 06:00:00
    const url = `${this.urlIox}/api/v1/sensor_logs/${measurement}/${date}`;
    const body = { sensorIds: `['${sensorIds.join("', '")}']` };

    const params = new HttpParams().set("sensorIds", JSON.stringify(sensorIds));

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post(url, body);
  }

  getSensorPrByDateRange(
    stationCode: string,
    start: string,
    end: string
  ): Observable<any> {
    const url = `${this.urlCompute}/iox-compute-service/api/v1/performance_ratio/calculation/actual/daterange`;
    const body = {
      start_datetime: start,
      end_datetime: end,
      station_code: stationCode,
    };

    // const params = new HttpParams().set("sensorIds", JSON.stringify(sensorIds));

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post(url, body);
  }

  delete(
    time: string,
    user: string,
    measurement: string,
    sensorId,
    remark: string
  ) {
    return this.http.delete<any[]>(
      this.urlIox +
        "/api/v1/logs/delete/" +
        measurement +
        "/" +
        sensorId +
        "/" +
        time +
        "/" +
        user +
        "/" +
        remark
    );
  }

  patch(
    time: string,
    measurement: string,
    sensorId,
    user: string,
    remark: string
  ) {
    return this.http.patch<any[]>(
      this.urlIox +
        "/api/v1/logs/undo_delete/" +
        measurement +
        "/" +
        sensorId +
        "/" +
        time +
        "/" +
        user +
        "/" +
        remark,
      null
    );
  }
  editData(
    time: string,
    measurement: string,
    sensorId,
    data: Object,
    user: string,
    remark: string
  ): Observable<any> {
    return this.http.patch<any[]>(
      this.urlIox +
        "/api/v1/logs/edit_data/" +
        measurement +
        "/" +
        sensorId +
        "/" +
        time +
        "/" +
        user +
        "/" +
        remark,
      data
    );
  }

  getUserManual(): Observable<Blob> {
    const url = this.urlIox + "/api/v1/get-manual";
    return this.http.get(url, {
      responseType: "blob",
      headers: new HttpHeaders().append("Content-Type", "application/pdf"),
    });
  }

  getCMreport(id, body): any {
    const url = this.urlIox + "/api/v1/cm-report/" + id;
    return this.http.post(url, body, { responseType: "text" });
  }

  getCMReportFiles(customerPrefix: string, cmReportId: string): any {
  const url = this.urlIox + `/api/v1/cm-report-files/${customerPrefix}/${cmReportId}`;
  return this.http.get(url); // Returns an array of { type, url, filename }
  }

  deleteCMReportFile(customerPrefix: string, filename: string): any {
  const url = this.urlIox + `/api/v1/cm-report-files/${customerPrefix}/${filename}`;
  return this.http.delete(url); // Returns 204 on success
  }

  downloadCMreport(id, body): Observable<Blob> {
    // const url = "http://localhost:5000/" + "/api/v1/cm-report-pdf/" + id;
    const url = this.urlIox + "/api/v1/cm-report-pdf/" + id;
    return this.http.post(url, body, {
      responseType: "blob",
    });
  }

  uploadCMReportFiles(formData, id) {
    const url = this.urlIox + "/api/v1/upload-cm-report/" + id;
    return this.http.post(url, formData);
  }

  exportPlantDevicesData(
    sensorData: any,
    fromDate: string,
    toDate: string,
    exportMode: string,
    interval: string,
    fieldsList: any[]
  ): Observable<Blob> {
    const url = `${this.urlIox}/api/v1/export-plant-devices`;
    const payload = {
      sensor_data: sensorData,
      from_date: fromDate,
      to_date: toDate,
      export_mode: exportMode,
      interval: interval,
      fields_list: fieldsList,
    };

    return this.http.post(url, payload, {
      responseType: "blob", // Expect a binary response (file)
    });
  }

  getLineUsers() {
    const url = this.urlIox + `/api/v1/line-users`;
    return this.http.get<any[]>(url);
  }

  muteAlarm(body) {
    const url = this.urlIox + `/api/v1/mute-alarm`;
    return this.http.post(url, body);
  }

  notifyAlarm(body) {
    const url = this.urlIox + `/api/v1/notify-alarm`;
    return this.http.post(url, body);
  }

  getAlarmDetails(id) {
    const url = this.urlIox + `/api/v1/alarm-details/${id}`;
    return this.http.get<Alarm>(url);
  }

  getAllAlarms() {
    const url = this.urlIox + `/api/v1/alarms-pg`;
    return this.http.get<any[]>(url);
  }

  getStationAlarms(codes: string[]) {
    return this.http.post<any[]>(
      `${this.urlIox}/api/v1/alarms-pg/by-stations`, // or whatever your path is
      { stationCodes: codes }
    );
  }

  deleteCMReport(customerPrefix,reportId){
    return this.http.delete(`${this.urlIox}/api/v1/cm-report/${customerPrefix}/${reportId}`)
  }
}

// getUserManual(): Observable<Blob> {
//   const httpOptions = {
//     headers: new HttpHeaders({
//       Accept: "application/pdf",
//     }),
//     responseType: "blob" as "json",
//   };
//   return this.http.get<Blob>(this.urlIox + "/api/v1/get-manual", httpOptions);
// }
// }
